// Lightweight API + local storage helpers for Dailyworkjobs.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send session cookie with every request (needed for auth-gated endpoints).
axios.defaults.withCredentials = true;

export async function fetchJobs({ bbox, limit } = {}) {
    // Cache-buster: prevents ISPs, corporate proxies and stale CDN edges from
    // returning an old small response. Optional bbox+limit lets mobile clients
    // request only the pins in their current viewport.
    const bust = Date.now();
    let lastErr = null;
    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            const params = { _: bust + attempt };
            if (bbox) params.bbox = bbox;
            if (limit) params.limit = limit;
            const r = await axios.get(`${API}/jobs`, {
                params,
                timeout: 20000,
                headers: { "Cache-Control": "no-cache" },
            });
            return r.data.jobs || [];
        } catch (e) {
            lastErr = e;
            // Brief pause before retry — most flaky networks recover in < 1s.
            await new Promise((res) => setTimeout(res, 400));
        }
    }
    throw lastErr;
}

export async function fetchApplyUrl(id) {
    try {
        const r = await axios.get(`${API}/apply`, { params: { id } });
        return r.data.url || "";
    } catch {
        return "";
    }
}

export async function submitLead(payload) {
    return axios.post(`${API}/lead`, payload);
}

export async function fetchHealth() {
    const r = await axios.get(`${API}/health`);
    return r.data;
}

export async function fetchAllLeads() {
    const r = await axios.get(`${API}/leads`);
    return r.data.leads || [];
}

export async function fetchAllUsers() {
    const r = await axios.get(`${API}/users`);
    return r.data.users || [];
}

export async function fetchMyApplications() {
    const r = await axios.get(`${API}/my/applications`);
    return r.data.applications || [];
}

export async function saveJob(jobId) {
    return axios.post(`${API}/my/saved/${encodeURIComponent(jobId)}`);
}

export async function unsaveJob(jobId) {
    return axios.delete(`${API}/my/saved/${encodeURIComponent(jobId)}`);
}

export async function fetchSavedJobs() {
    const r = await axios.get(`${API}/my/saved`);
    return r.data.saved || [];
}

export async function fetchSavedIds() {
    try {
        const r = await axios.get(`${API}/my/saved/ids`);
        return r.data.ids || [];
    } catch {
        return [];
    }
}

export async function updateMyProfile(payload) {
    const r = await axios.post(`${API}/my/profile`, payload);
    return r.data;
}

export async function geocodeSearch(q) {
    try {
        const r = await axios.get(`${API}/geocode`, { params: { q } });
        return r.data.results || [];
    } catch {
        return [];
    }
}

// ---- Local storage helpers ----
const LEAD_KEY = "dwj_lead_v1";
const LOCAL_LEADS_KEY = "dwj_leads_v1";
const COUNTRY_KEY = "dwj_country";
const SEEN_ONBOARDING = "dwj_seen_onboarding";

// Sensitive lead data (name/phone/email captured for autofill) lives in sessionStorage —
// scoped to the current tab and cleared automatically when the browser is closed,
// so XSS-vulnerable long-lived localStorage never holds this info.
const _sessionSafe = typeof sessionStorage !== "undefined" ? sessionStorage : null;
const _localSafe = typeof localStorage !== "undefined" ? localStorage : null;

export function getSavedLead() {
    if (!_sessionSafe) return null;
    try {
        const s = _sessionSafe.getItem(LEAD_KEY);
        return s ? JSON.parse(s) : null;
    } catch (e) {
        console.warn("[dwj] getSavedLead parse error:", e?.message || e);
        return null;
    }
}

export function saveLead(lead) {
    if (!_sessionSafe) return;
    try {
        _sessionSafe.setItem(LEAD_KEY, JSON.stringify(lead));
    } catch (e) {
        console.warn("[dwj] saveLead storage error:", e?.message || e);
    }
}

export function clearSavedLead() {
    try {
        _sessionSafe && _sessionSafe.removeItem(LEAD_KEY);
        // Also purge any legacy localStorage copy from earlier versions.
        _localSafe && _localSafe.removeItem(LEAD_KEY);
    } catch (e) {
        console.warn("[dwj] clearSavedLead error:", e?.message || e);
    }
}

export function pushLocalLead(entry) {
    // Local mirror of submitted leads for the offline/admin fallback view.
    // Uses sessionStorage so PII (name/phone/email) stays scoped to the current tab
    // and clears automatically when the browser is closed.
    if (!_sessionSafe) return;
    try {
        const list = JSON.parse(_sessionSafe.getItem(LOCAL_LEADS_KEY) || "[]");
        list.unshift(entry);
        _sessionSafe.setItem(LOCAL_LEADS_KEY, JSON.stringify(list.slice(0, 2000)));
        // Purge any legacy localStorage copy from earlier versions.
        _localSafe && _localSafe.removeItem(LOCAL_LEADS_KEY);
    } catch (e) {
        console.warn("[dwj] pushLocalLead error:", e?.message || e);
    }
}

export function getLocalLeads() {
    if (!_sessionSafe) return [];
    try {
        return JSON.parse(_sessionSafe.getItem(LOCAL_LEADS_KEY) || "[]");
    } catch {
        return [];
    }
}

export function getCountry() {
    if (!_localSafe) return null;
    try {
        return _localSafe.getItem(COUNTRY_KEY);
    } catch {
        return null;
    }
}

export function setCountry(cc) {
    if (!_localSafe) return;
    try {
        _localSafe.setItem(COUNTRY_KEY, cc);
    } catch { /* ignore */ }
}

export function hasSeenOnboarding() {
    if (!_localSafe) return false;
    try {
        return !!_localSafe.getItem(SEEN_ONBOARDING);
    } catch {
        return false;
    }
}
export function markOnboardingSeen() {
    if (!_localSafe) return;
    try {
        _localSafe.setItem(SEEN_ONBOARDING, "1");
    } catch {
        /* ignore */
    }
}

// Detect country from timezone/language. Default to USA.
export function detectCountry() {
    const saved = getCountry();
    if (saved) return saved;
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        const lang = navigator.language || "";
        if (
            /Toronto|Vancouver|Montreal|Edmonton|Winnipeg|Halifax|St_Johns|Regina|Yellowknife|Iqaluit/.test(tz) ||
            /-CA\b/.test(lang)
        ) {
            return "ca";
        }
    } catch {
        /* tz detect failed, fall back to us */
    }
    return "us";
}

// Faux favicon URL from company name (best-effort).
// Only returns a URL when we can confidently guess the domain — either the
// company matches a known-brands mapping OR an explicit domain is passed in.
// This prevents the map from showing Google's generic globe placeholder for
// unknown small businesses (better UX to fall back to the category icon).
export function faviconFor(company, options = {}) {
    if (!company) return null;
    const c = company.trim().toLowerCase();
    // Known mapping to common domains
    const known = {
        walmart: "walmart.com",
        target: "target.com",
        amazon: "amazon.com",
        starbucks: "starbucks.com",
        "mcdonald's": "mcdonalds.com",
        mcdonalds: "mcdonalds.com",
        chipotle: "chipotle.com",
        "dunkin'": "dunkindonuts.com",
        dunkin: "dunkindonuts.com",
        subway: "subway.com",
        "burger king": "bk.com",
        wendys: "wendys.com",
        "wendy's": "wendys.com",
        kroger: "kroger.com",
        costco: "costco.com",
        fedex: "fedex.com",
        ups: "ups.com",
        dhl: "dhl.com",
        uber: "uber.com",
        doordash: "doordash.com",
        instacart: "instacart.com",
        aldi: "aldi.us",
        publix: "publix.com",
        cvs: "cvs.com",
        walgreens: "walgreens.com",
        "home depot": "homedepot.com",
        lowes: "lowes.com",
        "lowe's": "lowes.com",
        marriott: "marriott.com",
        hilton: "hilton.com",
        hyatt: "hyatt.com",
        loblaws: "loblaws.ca",
        "tim hortons": "timhortons.com",
        canadian: "canadiantire.ca",
        shoppers: "shoppersdrugmart.ca",
    };
    let domain = known[c];
    // Only allow "guess" mode (slugify company name → .com) when the caller opts in.
    // Default is strict: unknown company → no logo, so we cleanly fall back to the category icon.
    if (!domain && options.allowGuess) {
        const slug = c
            .replace(/[^a-z0-9 ]/g, "")
            .replace(/\s+/g, "")
            .slice(0, 24);
        if (!slug || slug.length < 3) return null;
        domain = `${slug}.com`;
    }
    if (!domain) return null;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
}

// Normalize a string for search matching (accent-insensitive).
export function normalize(str) {
    return (str || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
}
